import React, { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Switch, useLocation } from 'react-router-dom'

import PrivateRoute from '../HOC/PrivateRoute'
import Header from '../components/Company/Header/Header'
import ProfileAside from '../components/ProfileAside/ProfileAside'
import { getUserIdFromUrl } from '../components/SearchTabsPanel/utils'
import useIndicateBrowserTab from '../hooks/useIndicateBrowserTap'
import useActiveCompany from '../hooks/useActiveCompany'
import { isMobileDevice } from '../helpers/mobileDevice.helper'
import { isDevEnvironment } from '../helpers/helpers'
import { getProfileRoutes } from '../constants/routesConfig'
import { profilePaths } from '../constants/constants'
import StatisticModal from '../components/StatisticModal/StatisticModal'
import { setSurveyStatusAction } from '../redux/actions/ui.action'
import Socket from '../websocket'
import ModerationStatus from '../components/ModerationStatus/ModerationStatus'

export const Profile = () => {
  const isAuth = useSelector(({ authentication }) => authentication.isAuth)
  const username = useSelector(({ user }) => user.profile.first_name)
  const userAccess = useSelector(({ user }) => user.profile.permissions)
  const userHasPermission = userAccess?.find((perm) => perm.name === 'vacancies')
  const currentStep = useSelector(({ ui }) => ui.currentStep)
  const { tabs } = useSelector(({ seafarersSearch }) => seafarersSearch)
  const hasSurvey = useSelector(({ ui }) => ui.hasSurvey)
  const location = useLocation()
  const dispatch = useDispatch()
  useIndicateBrowserTab(username)
  const surveyData = useSelector(({ ui }) => ui.surveyData)
  const surveyVacancyData = useSelector(({ ui }) => ui.surveyVacancyData)
  const companyData = useSelector(({ company }) => company)
  const isModerationNotificationVisible = !companyData.is_active
  const isModerationUnderTabsPage =
    location.pathname.startsWith(profilePaths.seafarers) ||
    location.pathname.startsWith(profilePaths.vacancy_manager) ||
    location.pathname.startsWith(profilePaths.users)

  useEffect(() => {
    if (surveyData && surveyData.length > 0) {
      Socket.emit('company_survey.get_survey', {
        vacancy_id: surveyData[0],
      })
    }
  }, [surveyData])
  const isActiveCompany = useActiveCompany()

  const getNewUrlAtCardIdPresence = (pathname, search) => {
    const params = new URLSearchParams(search)
    const cardIdFromUrlQuery = params.get('card_id')
    const tabNameFromUrlQuery = params.get('tab_name')

    if (!cardIdFromUrlQuery && !tabNameFromUrlQuery) {
      const cardId = getUserIdFromUrl(pathname)

      const clearUrl = pathname
        .split('/')
        .reduce((acc, cur) => {
          return cur !== String(cardId) ? [...acc, cur] : acc
        }, [])
        .join('/')

      const tabTitle = tabs.find((tab) => tab.id === cardId)?.title

      const queryStr = `/?card_id=${cardId}&tab_name=${tabTitle}`

      const newUrl = encodeURIComponent(`${clearUrl}${queryStr}`)

      return cardId && tabTitle ? newUrl : clearUrl
    }

    return encodeURIComponent(`${pathname}/${search}`)
  }

  const handleCloseSurveyModal = () => {
    const vacancyId = surveyData ? surveyData[0] : null

    if (currentStep !== 3 && vacancyId) {
      dispatch(setSurveyStatusAction(false))
    } else {
      if (surveyData && surveyData.length > 0) {
        Socket.emit('company_survey.select_seafarers', {
          vacancy_id: vacancyId,
          seafarer_ids: [],
        })
      } else {
        dispatch(setSurveyStatusAction(false))
      }
    }
  }

  const callbackUrl = getNewUrlAtCardIdPresence(location.pathname, location.search)

  const profileRoutes = getProfileRoutes()
  const routes = Object.keys(profileRoutes)

  const redirectLink =
    [
      profilePaths.seafarers,
      profilePaths.vacancy_manager,
      profilePaths.users,
      profilePaths.company_data,
      profilePaths.billing,
      profilePaths.statistic,
      profilePaths.settings,
    ].find((path) => routes.includes(path)) || profilePaths.company_data

  if (!isAuth) {
    return <Redirect to={`${profilePaths.auth}/?callback_url=${callbackUrl}`} />
  }

  if (isMobileDevice() && !isDevEnvironment()) return <Redirect to="/" />

  return (
    <div className="page profile-page">
      {hasSurvey && userHasPermission?.access_all && surveyData && surveyVacancyData && (
        <StatisticModal
          onClose={handleCloseSurveyModal}
          surveyData={surveyData}
          surveyVacancyData={surveyVacancyData}
        />
      )}

      <ProfileAside />

      <div className="profile-page-tab">
        <Header />
        {isModerationNotificationVisible && !isModerationUnderTabsPage && (
          <div className="profile-moderation-status container" style={{ marginTop: '20px' }}>
            <ModerationStatus data={companyData} />
          </div>
        )}

        <Switch>
          {routes.map((path) => {
            const { exact, Component } = profileRoutes[path]

            return <PrivateRoute key={path} exact={exact} path={path} component={Component} />
          })}

          <Redirect to={redirectLink} />
        </Switch>
      </div>
    </div>
  )
}
